import React, {useEffect, useState} from "react";
import {Icon} from "../../../components";
import "./_estate-card.scss";
import {
    DistributionType,
    FiduciaryMemberType,
    FlowchartEstateItemType,
    MemberType
} from "../../model/EstateFlowchartTypes";
import classNames from "classnames";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {setSideDrawerDetails, setSideDrawerOpen, sideDrawerDetailsSelector} from "../../slices/EstateFlowChartSlice";
import {NO_OP} from "../../../constants/common";
import {selectReleaseToggles} from "../../../ReleaseToggles/releaseTogglesSlice";
import {useAnalyticsTrackers} from "../../../utils/useAnalyticsTrackers";

export enum EstateCardViewModeType {
    SIMPLE = "SIMPLE",
    STANDARD = "STANDARD",
}

export enum EstateTaxDueType {
    FIRST_DEATH = "FIRST_DEATH",
    SECOND_DEATH = "SECOND_DEATH",
}

type DistributionStyles = {
    isTrustDistribution: boolean,
    icon: string,
    color: string
}

export type EstateCardRole = {
    type: MemberType | FiduciaryMemberType,
    value: string
}

export type EstateCardProps = {
    viewMode: EstateCardViewModeType,
    sequenceNumber?: number,
    selectedCardRef?: React.RefObject<HTMLDivElement>,
    isCardSelected?: boolean,
    estateItem: {
        parentEstateItemId: string,
        parentEstateItemType: FlowchartEstateItemType,
        id: string | undefined,
        distributionType: DistributionType,
        title: string,
        roles: EstateCardRole[],
        tag?: string,
        body?: {
            header: string,
            content: string
        }
    }
}

const getDistributionStyles = (distributionType: DistributionType, parentEstateItemType?: FlowchartEstateItemType): DistributionStyles => {
    const isParentTypeMiscellaneous = parentEstateItemType === FlowchartEstateItemType.MISCELLANEOUS;
    switch (distributionType) {
        case DistributionType.CHARITY:
            return {isTrustDistribution: isParentTypeMiscellaneous, icon: "heart", color: "#D6ACCF"};
        case DistributionType.OUTRIGHT:
            return {isTrustDistribution: isParentTypeMiscellaneous, icon: "account_switch", color: "#F1B040"};
        case DistributionType.WILL:
            return {isTrustDistribution: true, icon: "file", color: '#A5CF68'};
        case DistributionType.MISCELLANEOUS:
            return {isTrustDistribution: true, icon: "cards-star", color: "#808182"};
        case DistributionType.TRUST:
            return {isTrustDistribution: true, icon: "bank", color: "#05676E"};
        default:
            return {isTrustDistribution: true, icon: "bank", color: "#05676E"}; // Default to trust icon
    }
}

export const EstateCard: React.FC<EstateCardProps> = (
    {viewMode, estateItem, sequenceNumber, selectedCardRef, isCardSelected}
) => {
    const [selected, setSelected] = useState(false);
    const distributionStyles = getDistributionStyles(estateItem.distributionType, estateItem.parentEstateItemType);
    const dispatch = useAppDispatch();
    const sideDrawerDetails = useAppSelector(sideDrawerDetailsSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const {page} = useAnalyticsTrackers();

    useEffect(() => {
        if (!sideDrawerDetails) {
            setSelected(false);
        }
    }, [sideDrawerDetails]);

    const generateIcon = () => {
        if (estateItem.distributionType === DistributionType.CHARITY) {
            return (
                <img
                    data-testid="heart-icon"
                    src="/heart.png"
                    alt="heart icon"
                    width="18px"
                    height="18px"
                    className="heart"
                />
            );
        } else if (estateItem.distributionType === DistributionType.MISCELLANEOUS) {
            return (
                <img
                    data-testid="cards-star-icon"
                    src="/cards_star.svg"
                    alt="cards star icon"
                    width="18px"
                    height="18px"
                    className="cards-star"
                />
            );
        }
        return <Icon name={distributionStyles.icon} style={{color: distributionStyles.color}}/>;
    };


    function isNonDistributionStandard() {
        return !distributionStyles.isTrustDistribution &&
            viewMode === EstateCardViewModeType.STANDARD;
    }

    const generateRoles = () =>
        estateItem.roles.slice(0, 2).map((role, index) => {
            const roleName =
                role.type === FiduciaryMemberType.Guardian
                    ? "GUARDIAN FOR MINORS"
                    : role.type;

            const classStyle = isNonDistributionStandard()
                ? "estate-card-role-nonDistribution-standard"
                : "estate-card-role";

            return (<div key={role.type + index} className={classStyle}>
                <div className="role-type">{roleName}</div>
                <div className="role-value">{role.value}</div>
            </div>);
        });

    const generateTag = () => {
        let tag = estateItem.tag;
        if (releaseToggles?.enableEstateFlowchartMVPRelease) {
            if (tag === "Both") tag = "Exempt & Non-Exempt";
            if (tag === "Unknown/Inapplicable") tag = undefined;
        }
        return tag ? <div className="tag">{tag}</div> : undefined;
    }

    const handleOnClick = () => {
        const isParentTypeCard = estateItem.parentEstateItemId === estateItem?.id;
        dispatch(setSideDrawerOpen(!selected));
        dispatch(setSideDrawerDetails({
            parentEstateItemId: estateItem.parentEstateItemId,
            parentEstateItemType: estateItem.parentEstateItemType,
            id: estateItem.id,
            cardType: isParentTypeCard ? estateItem.parentEstateItemType : estateItem.distributionType,
            title: estateItem.title,
        }));
        console.log("Clicked on card: ", estateItem.id, estateItem.title, estateItem.distributionType);
        page(`DEF_EstateCard_Click`);
        setSelected(!selected);
    }

    return (
        <div key={`estate-card-${estateItem.id}-${sequenceNumber}`}
             id={`estate-card-${estateItem.id}`}
             data-testid={`estate-card-${estateItem.id}`}
             data-parent-id={estateItem.parentEstateItemId}
             data-parent-estate-item-type={estateItem.id !== "placeHolder-card-id" ? estateItem.parentEstateItemType : ""}
             aria-label={"estate card"}
             className={classNames("estate-card",
                 {"placeHolder": estateItem.id === "placeHolder-card-id"},
                 {"non-trust-distribution-card": !distributionStyles.isTrustDistribution},
                 {
                     "first-level-estate-item-card": viewMode === EstateCardViewModeType.STANDARD
                         && estateItem.parentEstateItemId === estateItem?.id
                 },
                 {"simple-mode": viewMode === EstateCardViewModeType.SIMPLE},
                 {"standard-mode": viewMode === EstateCardViewModeType.STANDARD},
             )}
             style={{
                 borderTop: `4px solid ${distributionStyles.color}`,
                 backgroundColor: (selected || isCardSelected) ? "#E6F0F1" : "#FFF"
             }}
             ref={isCardSelected ? selectedCardRef : null}
             onClick={estateItem.id === "placeHolder-card-id" ? NO_OP : handleOnClick}
             aria-hidden="true">

            <div className="estate-card-header">
                {generateIcon()}
                {generateTag()}
            </div>
            <h4 className={viewMode === EstateCardViewModeType.STANDARD ? "estate-card-title-standard" : "estate-card-title-simple"}>
                {estateItem.title}
            </h4>
            {(!distributionStyles.isTrustDistribution || viewMode === EstateCardViewModeType.STANDARD) &&
                <div className="estate-card-roles">{generateRoles()}</div>}
            {viewMode === EstateCardViewModeType.STANDARD && estateItem.body && (
                <div className="estate-card-body">
                    <span className="body-content">
                        <span className="body-header">{estateItem.body.header}:</span> {estateItem.body.content}
                    </span>
                </div>
            )}
        </div>
    )
};